import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../utils/api';

// ── Hook persistance filtres (sessionStorage) ─────────────────
export function usePersistedState(key, defaultValue) {
  const [state, setState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('filter_' + key);
      return saved !== null ? JSON.parse(saved) : defaultValue;
    } catch { return defaultValue; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('filter_' + key, JSON.stringify(state)); }
    catch {}
  }, [key, state]);
  return [state, setState];
}

// ── Bouton Retour ─────────────────────────────────────────────
export function BackButton({ label = '← Retour' }) {
  const navigate = useNavigate();
  return (
    <button className="btn-secondary" onClick={() => navigate(-1)}>{label}</button>
  );
}

// ── Messages de résultat : VERT = action réussie, AMBRE = avertissement, ROUGE = échec (mêmes teintes partout) ──────────
// niveauMessage(texte) → 'succes' | 'attention' | 'echec' :
//  • échec      : « ❌ », « Erreur », « Impossible », « échec », « n'a pas pu »… (même précédé d'un ⚠)
//  • avertissement : « ⚠ … » ou « Attention… » sans mot d'échec — dont un succès partiel (« ✓ Email renvoyé — ⚠ SMS non envoyé »)
//  • succès     : « ✓ », « ✅ » ou tout message sans signe ni mot d'échec (« Email envoyé avec succès. »)
export function niveauMessage(texte) {
  const t = String(texte ?? '').trim();
  if (!t) return 'succes';
  if (/(❌|⛔|🚫)/.test(t) || /^(✗|✘|✕)/.test(t)) return 'echec';
  if (/^(erreur|échec|echec|impossible|refus|invalide|veuillez|aucun|aucune)/i.test(t)) return 'echec';
  if (/(échou|echou|échec|erreur|impossible|n'a pas pu|n'ont pas pu|pas été (envoy|enregistr|cré|import)|refus|introuvable|invalide|expiré|obligatoire)/i.test(t)) return 'echec';
  if (/⚠/.test(t) || /^attention/i.test(t)) return 'attention';
  return 'succes';
}
export const COULEUR_SUCCES = '#34d399';
export const COULEUR_ATTENTION = '#fbbf24'; // ambre
export const COULEUR_ECHEC = '#f87171';
// Accepte un niveau ('succes' | 'attention' | 'echec') ou un booléen (true = succès, false = échec)
function niveauNormalise(n) { return n === true ? 'succes' : (n === false || n === undefined || n === null) ? 'echec' : n; }
export function couleurMessage(niveau) {
  const n = niveauNormalise(niveau);
  return n === 'succes' ? COULEUR_SUCCES : n === 'attention' ? COULEUR_ATTENTION : COULEUR_ECHEC;
}
// Fond teinté + texte + bordure, à étaler dans un style : style={{ padding: …, ...styleMessage(niveau) }}
export function styleMessage(niveau) {
  const n = niveauNormalise(niveau);
  if (n === 'succes') return { background: 'rgba(52,211,153,0.12)', color: COULEUR_SUCCES, border: '1px solid rgba(52,211,153,0.35)' };
  if (n === 'attention') return { background: 'rgba(251,191,36,0.12)', color: COULEUR_ATTENTION, border: '1px solid rgba(251,191,36,0.35)' };
  return { background: 'rgba(248,113,113,0.12)', color: COULEUR_ECHEC, border: '1px solid rgba(248,113,113,0.35)' };
}
export const STYLE_ATTENTION = styleMessage('attention');

// ── Bouton d'action avec confirmation optionnelle et état « en cours » ──
// onConfirm : fonction async qui gère elle-même ses erreurs et ses messages
export function BoutonRegenerer({ label = '🔄 Régénérer', confirmMessage, onConfirm, disabled }) {
  const [busy, setBusy] = useState(false);
  async function handleClick() {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  }
  return (
    <button className="btn-secondary" onClick={handleClick} disabled={busy || disabled}>
      {busy ? '⏳...' : label}
    </button>
  );
}

// ── Stat Card ────────────────────────────────────────────────
export function StatCard({ label, value, icon, accent, subtitle }) {
  return (
    <div className={`stat-card ${accent || ''}`}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-body">
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
        {subtitle && <div className="stat-subtitle">{subtitle}</div>}
      </div>
    </div>
  );
}

// ── Data Table ───────────────────────────────────────────────
export function DataTable({ columns, data, onRowClick, rowStyle, emptyMessage = 'Aucune donnée' }) {
  if (!data || data.length === 0) {
    return <div className="empty-state">{emptyMessage}</div>;
  }
  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th key={i} className={col.mobileHide ? 'col-mobile-hide' : ''} style={col.width ? { width: col.width } : {}}>{col.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} onClick={() => onRowClick?.(row)} className={onRowClick ? 'clickable' : ''} style={rowStyle ? rowStyle(row) : {}}>
              {columns.map((col, j) => (
                <td key={j} className={col.mobileHide ? 'col-mobile-hide' : ''} data-label={col.header}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Badge / Pill ─────────────────────────────────────────────
const BADGE_COLORS = {
  'Validé': 'badge-success',
  'Brouillon': 'badge-neutral',
  'Envoyé': 'badge-info',
  'Refusé': 'badge-danger',
  'Présent': 'badge-success',
  'Absent': 'badge-danger',
  'Retard': 'badge-warning',
  'Client': 'badge-success',
  'Prospect': 'badge-info',
  'Professionnel': 'badge-neutral',
  'Particulier': 'badge-accent',
};

export function Badge({ children, variant }) {
  const cls = variant || BADGE_COLORS[children] || 'badge-neutral';
  return <span className={`badge ${cls}`}>{children}</span>;
}

// ── Search Bar ───────────────────────────────────────────────
export function SearchBar({ value, onChange, placeholder = 'Rechercher...' }) {
  return (
    <div className="search-bar">
      <span className="search-icon">⌕</span>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {value && <button className="search-clear" onClick={() => onChange('')}>×</button>}
    </div>
  );
}

// ── Page Header ──────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="page-header">
      <div>
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

// ── Pagination ───────────────────────────────────────────────
export function Pagination({ page, total, limit, onChange }) {
  const totalPages = Math.ceil(total / limit);
  if (totalPages <= 1) return null;
  return (
    <div className="pagination">
      <button disabled={page <= 1} onClick={() => onChange(page - 1)}>‹ Préc.</button>
      <span className="pagination-info">Page {page} / {totalPages} — {total} résultats</span>
      <button disabled={page >= totalPages} onClick={() => onChange(page + 1)}>Suiv. ›</button>
    </div>
  );
}

// ── Loading ──────────────────────────────────────────────────
export function Loading() {
  return (
    <div className="loading-state">
      <div className="loading-spinner" />
      <span>Chargement...</span>
    </div>
  );
}

// ── Filter Chip ──────────────────────────────────────────────
export function FilterChip({ label, active, onClick }) {
  return (
    <button className={`filter-chip ${active ? 'active' : ''}`} onClick={onClick}>
      {label}
    </button>
  );
}

// ── Visionneuse de documents (aperçu unifié) ─────────────────
// <VisionneuseDocuments /> est monté une seule fois dans App.jsx ;
// partout ailleurs : ouvrirDocument({ titre, url, pdfUrl?, nomFichier?, method?, body?, actions?, libelleTelechargement? })
// - url    : document à afficher (HTML, PDF ou image), chargé avec le jeton
// - pdfUrl : si fourni, le bouton « ⬇ PDF » télécharge ce PDF ; sinon « ⬇ Télécharger » le document affiché
// - libelleTelechargement : remplace « ⬇ PDF » quand pdfUrl est fourni (ex. fichier ZIP)
// - les formats non affichables (zip, Word…) sont téléchargés directement
const API_DOCS = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

function urlComplete(url) {
  if (/^https?:\/\//.test(url)) return url;
  if (url.startsWith('/api/')) return API_DOCS.replace(/\/api$/, '') + url;
  return API_DOCS + url;
}

function nomDepuisEntete(r, defaut) {
  const cd = r.headers.get('Content-Disposition') || '';
  const utf = cd.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf) return decodeURIComponent(utf[1]);
  const m = cd.match(/filename="?([^";]+)"?/i);
  if (!m) return defaut;
  try { return decodeURIComponent(m[1]); } catch { return m[1]; }
}

function telechargerBlob(blob, nom) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = nom || 'document';
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

async function chargerDocument(url, token, { method = 'GET', body } = {}) {
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const r = await fetch(urlComplete(url), {
    method, headers, credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || `Erreur ${r.status}`);
  }
  const type = (r.headers.get('Content-Type') || '').toLowerCase();
  let blob = await r.blob();
  if (type.includes('text/html')) blob = new Blob([blob], { type: 'text/html;charset=utf-8' });
  return { blob, type, nom: nomDepuisEntete(r, '') };
}

let hoteVisionneuse = null;

export function ouvrirDocument(opts) {
  if (hoteVisionneuse) hoteVisionneuse.ouvrir(opts);
}

export function fermerDocument() {
  if (hoteVisionneuse) hoteVisionneuse.fermer();
}

// ── Mise en forme des saisies (fiche d'inscription…) — même règle côté serveur (normaliserSaisieFiche) ──────────
// Nom de famille : MAJUSCULES. Prénom : minuscules avec majuscule initiale (aussi après espace, tiret, apostrophe).
// Téléphone : chiffres seuls, XX XX XX XX XX (10 chiffres) ; « +33 6… » et « 0033 6… » deviennent « 06… ».
export function formaterNomFamille(v) {
  return String(v ?? '').toLocaleUpperCase('fr-FR');
}
export function formaterPrenom(v) {
  return String(v ?? '').toLocaleLowerCase('fr-FR')
    .replace(/(^|[\s\-'’])(\p{L})/gu, (m, sep, l) => sep + l.toLocaleUpperCase('fr-FR'));
}
export function formaterTelephone(v) {
  const brut = String(v ?? '').trim();
  let d = brut.replace(/\D/g, '');
  if (/^(\+|00)33/.test(brut.replace(/\s/g, ''))) d = '0' + d.replace(/^(00)?33/, '').replace(/^0/, '');
  return d.slice(0, 10).replace(/(\d{2})(?=\d)/g, '$1 ');
}

// Libellé d'une version archivée de fiche d'inscription (nom de fichier : statut + marque éventuelle)
// « soumise_avantcej » = exemplaire reçu du client, conservé avant une modification de l'équipe
export function libelleArchiveFiche(statut) {
  const [base, marque] = String(statut || '').split('_');
  const etat = base === 'validee' ? 'validée' : base === 'soumise' ? 'reçue' : base;
  if (marque === 'avantcej') return `${etat} — version avant modification par CEJ (exemplaire du client)`;
  if (marque === 'supprimee') return `${etat} — fiche supprimée`;
  return etat;
}

// Aperçus uniformes : mêmes routes et même visionneuse partout (page devis, page session…)
export function ouvrirApercuDevis(devisId, { actions } = {}) {
  const id = encodeURIComponent(devisId);
  ouvrirDocument({ titre: devisId, url: `/devis/${id}/preview`, pdfUrl: `/devis/${id}/pdf`, nomFichier: `${devisId}.pdf`, actions });
}

export function ouvrirApercuConvention(conventionId, titre) {
  ouvrirDocument({
    titre: titre || `Convention ${conventionId}`,
    url: `/conventions/${conventionId}/preview`,
    pdfUrl: `/conventions/${conventionId}/pdf`,
    nomFichier: `convention_${conventionId}.pdf`,
    libelleTelechargement: '⬇ Télécharger',
  });
}

// Téléchargement direct (sans aperçu) d'un document authentifié
export async function telechargerDocument({ url, nomFichier }) {
  try {
    const token = localStorage.getItem('gf_token') || localStorage.getItem('gf_formateur_token');
    const { blob, nom } = await chargerDocument(url, token);
    telechargerBlob(blob, nom || nomFichier);
  } catch (e) { alert('Erreur téléchargement : ' + e.message); }
}

export function VisionneuseDocuments() {
  const { token } = useAuth();
  const [doc, setDoc] = React.useState(null);
  const [telechargement, setTelechargement] = React.useState(false);

  const requete = React.useRef(0);   // ignore un chargement terminé après fermeture
  const fermer = React.useCallback(() => { requete.current++; setDoc(null); }, []);

  const ouvrir = React.useCallback(async (opts) => {
    const { titre, url, pdfUrl, nomFichier, method, body, actions, libelleTelechargement } = opts;
    const n = ++requete.current;
    setDoc({ titre, pdfUrl, nomFichier, actions, libelleTelechargement, chargement: true });
    try {
      const { blob, type, nom } = await chargerDocument(url, token, { method, body });
      if (n !== requete.current) return;
      const affichable = type.includes('text/html') || type.includes('application/pdf') || type.startsWith('image/');
      if (!affichable) {
        telechargerBlob(blob, nom || nomFichier);
        setDoc(null);
        return;
      }
      setDoc({ titre, pdfUrl, actions, libelleTelechargement, blob, nomFichier: nom || nomFichier,
        estImage: type.startsWith('image/'), src: URL.createObjectURL(blob) });
    } catch (e) {
      if (n !== requete.current) return;
      setDoc(null);
      alert('Impossible d\'afficher le document : ' + e.message);
    }
  }, [token]);

  React.useEffect(() => () => { if (doc?.src) URL.revokeObjectURL(doc.src); }, [doc]);

  React.useEffect(() => {
    const hote = { ouvrir, fermer };
    hoteVisionneuse = hote;
    return () => { if (hoteVisionneuse === hote) hoteVisionneuse = null; };
  }, [ouvrir, fermer]);

  async function telecharger() {
    setTelechargement(true);
    try {
      if (doc.pdfUrl) {
        const { blob, nom } = await chargerDocument(doc.pdfUrl, token);
        telechargerBlob(blob, nom || (doc.nomFichier ? doc.nomFichier.replace(/\.html?$/i, '') + '.pdf' : 'document.pdf'));
      } else {
        telechargerBlob(doc.blob, doc.nomFichier);
      }
    } catch (e) { alert('Erreur téléchargement : ' + e.message); }
    finally { setTelechargement(false); }
  }

  const visionneuse = doc ? (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', padding: '0.75rem 1.5rem', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
        <h3 style={{ color: 'var(--text-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Aperçu — {doc.titre}</h3>
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          {doc.actions}
          {!doc.chargement && (
            <button onClick={telecharger} disabled={telechargement}
              style={{ padding: '0.4rem 0.9rem', borderRadius: '6px', background: 'var(--accent-blue)', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: telechargement ? 'default' : 'pointer', border: 'none', opacity: telechargement ? 0.7 : 1 }}>
              {telechargement ? '⏳' : doc.pdfUrl ? (doc.libelleTelechargement || '⬇ PDF') : '⬇ Télécharger'}
            </button>
          )}
          <button className="btn-secondary" onClick={fermer}>✕ Fermer</button>
        </div>
      </div>
      {doc.chargement
        ? <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>Chargement de l'aperçu…</div>
        : doc.estImage
          ? <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', overflow: 'auto', background: '#f0f0f0' }}>
              <img src={doc.src} alt={doc.titre} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </div>
          : <iframe src={doc.src} style={{ flex: 1, border: 'none', background: '#f0f0f0' }} title={`Aperçu ${doc.titre}`} />}
    </div>
  ) : null;

  return visionneuse;
}


// ── Installation PWA (PC / Android) ──────────────────────────
// Chrome/Edge émettent « beforeinstallprompt » quand l'appli est installable :
// on le mémorise pour proposer un bouton « Installer l'application ».
let invitationInstall = null;
const abonnesInstall = new Set();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', e => {
    e.preventDefault();
    invitationInstall = e;
    abonnesInstall.forEach(f => f(true));
  });
  window.addEventListener('appinstalled', () => {
    invitationInstall = null;
    abonnesInstall.forEach(f => f(false));
  });
}

export function BoutonInstallerApp({ className = 'btn-sidebar-action' }) {
  const [dispo, setDispo] = React.useState(!!invitationInstall);
  React.useEffect(() => {
    abonnesInstall.add(setDispo);
    return () => abonnesInstall.delete(setDispo);
  }, []);
  if (!dispo) return null;
  async function installer() {
    const ev = invitationInstall;
    if (!ev) return;
    ev.prompt();
    const { outcome } = await ev.userChoice;
    if (outcome === 'accepted') { invitationInstall = null; setDispo(false); }
  }
  return <button className={className} onClick={installer}>💻 Installer l'application</button>;
}

// ── Currency formatter ───────────────────────────────────────
// ── ConflictModal — modale de confirmation de conflit planning ───────────────
export function ConflictModal({ conflicts = [], label, onCancel, onConfirm, onModifier, modifierLabel, confirmLabel = 'Confirmer quand même', saving }) {
  const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '?';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.75rem', width: '500px', maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
          <span style={{ fontSize: '1.4rem' }}>⚠️</span>
          <h3 style={{ margin: 0, color: '#fbbf24', fontSize: '1rem' }}>
            Conflit de planning détecté{label ? ` — ${label}` : ''}
          </h3>
        </div>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.88rem' }}>
          Les ressources suivantes sont déjà occupées sur la période sélectionnée :
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
          {conflicts.map((c, i) => (
            <div key={i} style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: '8px', padding: '0.6rem 0.9rem' }}>
              <div style={{ fontWeight: 600, color: '#fbbf24', marginBottom: 2 }}>
                {c.type === 'salle' ? '🏫' : '👤'} {c.libelle}
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Session {c.session_id} — {c.intitule}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                {c.dates_conflit
                  ? <>Jour{c.dates_conflit.includes(',') ? 's' : ''} concerné{c.dates_conflit.includes(',') ? 's' : ''} : <strong style={{ color: 'var(--text-secondary)' }}>{c.dates_conflit}</strong></>
                  : <>Du {fmtDate(c.date_debut)} au {fmtDate(c.date_fin)}</>
                }
              </div>
            </div>
          ))}
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
          Voulez-vous quand même procéder ?
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={onCancel}>✕ Annuler</button>
          {onModifier && (
            <button
              className="btn-secondary"
              onClick={onModifier}
              style={{ border: '1px solid var(--accent-blue)', color: 'var(--accent-blue)' }}>
              {modifierLabel || '← Modifier'}
            </button>
          )}
          <button
            disabled={saving}
            onClick={onConfirm}
            style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: 'none', background: '#d97706', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}>
            ⚠️ {saving ? 'En cours...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Fiches d'inscription directes (sans devis) : liste + validation ───────────────────────────
// Partagé par la page Session (sous les inscrits : filtre sessionId) et la fiche Client (filtre clientId).
// La page fournit onRenvoyer / onNouvelle car elle possède la fenêtre d'envoi du lien.
export function FichesDirectesPanel({
  sessionId = null, clientId = null,
  titre = "Fiches d'inscription directes", sousTitre = 'sans devis',
  onRenvoyer, onNouvelle,
  onValide,                      // appelé après une validation réussie (ex. recharger les inscrits)
  refreshKey = 0,                // incrémenter pour recharger la liste
  masquerSiVide = false,
  liensSessionEnLecture = false, // fiche Client : un lien rattaché à une session se gère dans la session
  messageVide = "Aucun lien d'inscription directe envoyé.",
}) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [liens, setLiens] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [validatingId, setValidatingId] = useState(null);
  const [doublons, setDoublons] = useState(null);
  const [confirmations, setConfirmations] = useState([]);
  const [msg, setMsg] = useState('');
  const [archives, setArchives] = useState({}); // id du lien → versions précédentes de la fiche (PDF archivés)

  const filtre = sessionId ? `session_id=${encodeURIComponent(sessionId)}` : (clientId ? `client_id=${encodeURIComponent(clientId)}` : '');

  function charger() {
    if (!filtre) { setChargement(false); return; }
    fetch(`${API_DOCS}/inscription-liens?${filtre}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { const liste = d.data || []; setLiens(liste); chargerArchives(liste); })
      .catch(() => {})
      .finally(() => setChargement(false));
  }

  async function chargerArchives(liste) {
    const parLien = {};
    await Promise.all(liste.map(async il => {
      try {
        const r = await fetch(`${API_DOCS}/inscription-liens/${il.id}/fiche-archives`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json();
        if (d.data?.length) parLien[il.id] = d.data;
      } catch (e) { /* archives facultatives */ }
    }));
    setArchives(parLien);
  }

  async function supprimerFiche(il) {
    const validee = il.fiche_statut === 'validee';
    const message = `Supprimer la fiche d'inscription reçue pour « ${il.intitule_formation || 'cette formation'} » ?\n\n`
      + (validee ? `⚠️ Cette fiche a déjà été VALIDÉE : les stagiaires créés et les inscriptions déjà faites sont conservés.\n\n` : '')
      + `Une copie PDF de la fiche est conservée dans les versions précédentes. Le lien d'inscription reste utilisable : le client pourra renvoyer une fiche.`;
    if (!window.confirm(message)) return;
    try {
      const res = await fetch(`${API_DOCS}/fiches-inscription/${il.fiche_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Erreur lors de la suppression');
      afficher(d.archivee === false ? 'Fiche supprimée (la copie PDF n\'a pas pu être créée)' : '✓ Fiche supprimée — copie PDF conservée dans les versions précédentes', 6000);
      charger();
    } catch (e) { afficher('Erreur : ' + e.message, 6000); }
  }
  useEffect(charger, [filtre, refreshKey, token]); // eslint-disable-line react-hooks/exhaustive-deps

  function afficher(m, duree) { setMsg(m); if (duree) setTimeout(() => setMsg(''), duree); }

  async function valider(ficheId, conf) {
    setValidatingId(ficheId);
    try {
      const res = await fetch(`${API_DOCS}/fiches-inscription/${ficheId}/valider`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirmer_doublons: conf && conf.length > 0 ? conf : undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur validation');
      if (data.doublons?.length > 0) {
        setDoublons(data.doublons.map(d => ({ ...d, fiche_id: ficheId })));
        setConfirmations([]);
        return; // la validation reprend après le choix de l'utilisateur
      }
      setDoublons(null); setConfirmations([]); setValidatingId(null);
      const nb = data.stagiaires_crees ?? 0;
      if (data.avertissements?.length > 0) {
        // Stagiaires enregistrés mais inscription incomplète : on ne dit pas « validée » sans le signaler
        afficher(`Attention — fiche validée, ${nb} stagiaire(s) enregistré(s), ${data.inscrits ?? 0} inscrit(s) à la session. À vérifier : ${data.avertissements.join(' ')}`, 20000);
      } else {
        afficher(`✓ Fiche validée${nb > 0 ? ` — ${nb} stagiaire(s) enregistré(s)` : ''}${data.inscrits > 0 ? `, ${data.inscrits} inscrit(s) à la session` : ''}`, 5000);
      }
      charger();
      if (onValide) onValide(data);
    } catch (e) {
      setValidatingId(null);
      afficher('Erreur : ' + e.message, 6000);
    }
  }

  if (chargement) return null;
  if (liens.length === 0 && masquerSiVide) return null;

  const aValider = liens.filter(il => il.fiche_statut === 'soumise' && il.fiche_id).length;
  const couleurMsg = styleMessage(niveauMessage(msg));

  return (
    <>
      <div className="card" style={{ marginTop: '1rem', borderLeft: '3px solid #8b5cf6' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.6rem', flexWrap: 'wrap' }}>
          <h3 className="card-title" style={{ margin: 0, color: '#7c3aed' }}>
            {titre}
            {sousTitre && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', fontWeight: 400, color: '#94a3b8' }}>{sousTitre}</span>}
            {aValider > 0 && (
              <span style={{ marginLeft: '0.6rem', fontSize: '0.74rem', fontWeight: 700, color: '#fff', background: '#f59e0b', borderRadius: 10, padding: '0.1rem 0.55rem' }}>
                {aValider} à valider
              </span>
            )}
          </h3>
          {onNouvelle && (
            <button onClick={onNouvelle}
              style={{ padding: '0.4rem 0.9rem', borderRadius: '8px', border: 'none', background: '#8b5cf6', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
              + Nouvelle fiche
            </button>
          )}
        </div>

        {liens.length === 0 ? (
          <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', margin: '0.5rem 0' }}>{messageVide}</p>
        ) : liens.map(il => {
          const enSession = liensSessionEnLecture && il.session_id;
          const statut = il.fiche_statut === 'validee' ? ['✅ Validée', 'rgba(16,185,129,0.2)', '#065f46']
            : il.fiche_statut === 'soumise' ? ['✓ Fiche reçue', 'rgba(16,185,129,0.15)', '#059669']
            : il.statut === 'envoyé' ? ['Envoyé', 'rgba(59,130,246,0.15)', '#3b82f6']
            : ['Créé', 'rgba(148,163,184,0.15)', '#64748b'];
          return (
            <div key={il.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.8rem', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                  {sessionId && il.nom_client ? `${il.nom_client} — ` : ''}{il.intitule_formation || '—'}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  Créé le {new Date(il.created_at).toLocaleDateString('fr-FR')}
                  {il.email_client ? ` · ${il.email_client}` : ''}
                  {enSession ? ` · session ${il.session_id}` : (!sessionId && !il.session_id ? ' · aucune session liée' : '')}
                </div>
                {il.fiche_stagiaires?.length > 0 && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                    Stagiaire{il.fiche_stagiaires.length > 1 ? 's' : ''} : {il.fiche_stagiaires.map(st => `${st.nom} ${st.prenom}`.trim()).join(', ')}
                  </div>
                )}
                {archives[il.id]?.length > 0 && (
                  <div style={{ fontSize: '0.76rem', marginTop: 4 }}>
                    <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Versions précédentes (PDF archivé) :</span>
                    {archives[il.id].map(a => {
                      const quand = a.date ? new Date(a.date).toLocaleString('fr-FR') : a.nom;
                      return (
                        <div key={a.nom} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: 2 }}>
                          <span>{quand} — {libelleArchiveFiche(a.statut)}</span>
                          <button className="btn-secondary" style={{ padding: '0.1rem 0.5rem', fontSize: '0.74rem' }}
                            onClick={() => ouvrirDocument({ titre: `Fiche d'inscription — version du ${quand}`, url: `/inscription-liens/${il.id}/fiche-archives/${encodeURIComponent(a.nom)}`, nomFichier: a.nom })}>
                            👁 Aperçu
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <span style={{ fontSize: '0.78rem', padding: '0.15rem 0.55rem', borderRadius: '4px', fontWeight: 600, background: statut[1], color: statut[2] }}>{statut[0]}</span>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                <button onClick={() => { navigator.clipboard?.writeText(il.lien); afficher('Lien copié !', 2500); }}
                  style={{ padding: '0.2rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.76rem' }}>
                  📋 Copier
                </button>
                {enSession ? (
                  <button onClick={() => navigate(`/sessions/${il.session_id}`)}
                    style={{ padding: '0.2rem 0.6rem', borderRadius: '6px', border: 'none', background: '#0891b2', color: '#fff', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600 }}>
                    Ouvrir la session →
                  </button>
                ) : (
                  <>
                    {onRenvoyer && (
                      <button onClick={() => onRenvoyer(il)}
                        style={{ padding: '0.2rem 0.6rem', borderRadius: '6px', border: 'none', background: '#8b5cf6', color: '#fff', cursor: 'pointer', fontSize: '0.76rem' }}>
                        ✉ Renvoyer
                      </button>
                    )}
                    {il.fiche_statut === 'soumise' && il.fiche_id && (
                      <button onClick={() => valider(il.fiche_id, [])} disabled={validatingId === il.fiche_id}
                        style={{ padding: '0.2rem 0.6rem', borderRadius: '6px', border: 'none', background: '#059669', color: '#fff', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600 }}>
                        {validatingId === il.fiche_id ? '⏳...' : '✅ Valider'}
                      </button>
                    )}
                    {il.fiche_id && (
                      <>
                        <button
                          onClick={() => ouvrirDocument({
                            titre: `Fiche d'inscription — ${il.intitule_formation || ''}`,
                            url: `/fiches-inscription/${il.fiche_id}/pdf`,
                            nomFichier: 'fiche_inscription.pdf',
                            actions: (
                              <a href={il.lien} target="_blank" rel="noreferrer" onClick={fermerDocument}
                                style={{ display: 'inline-flex', alignItems: 'center', padding: '0.4rem 0.9rem', borderRadius: 6, background: '#7c3aed', color: '#fff', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}>
                                ✏️ Modifier la fiche
                              </a>
                            ),
                          })}
                          style={{ padding: '0.2rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.76rem' }}>
                          👁 Aperçu
                        </button>
                        <a href={il.lien} target="_blank" rel="noreferrer"
                          title="Ouvre le formulaire en mode équipe : l'exemplaire du client est conservé dans les versions précédentes"
                          style={{ padding: '0.2rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '0.76rem' }}>
                          ✏️ Modifier
                        </a>
                        <button onClick={() => supprimerFiche(il)}
                          style={{ padding: '0.2rem 0.6rem', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.5)', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: '0.76rem' }}>
                          🗑 Supprimer
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}

        {msg && (
          <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.82rem', ...couleurMsg }}>{msg}</div>
        )}
      </div>

      {/* Fenêtre des stagiaires déjà connus (doublons) : choix par stagiaire avant de valider */}
      {doublons && doublons.length > 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '1.5rem', maxWidth: 600, width: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
            <h3 style={{ color: '#f59e0b', marginBottom: '0.5rem' }}>⚠️ Stagiaires déjà enregistrés</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1rem' }}>
              Des correspondances ont été trouvées dans la base. Choisissez l'action pour chaque stagiaire.
            </p>
            {doublons.map(d => {
              const conf = confirmations.find(c => c.idx === d.idx);
              const choix = (action, libelle, aide, couleur, stagId) => (
                <button onClick={() => setConfirmations(prev => [...prev.filter(c => c.idx !== d.idx), { idx: d.idx, action, stagiaire_id: stagId }])}
                  style={{ padding: '0.45rem 0.6rem', borderRadius: 6, border: `2px solid ${conf?.action === action ? couleur : 'var(--border)'}`, background: conf?.action === action ? 'rgba(148,163,184,0.12)' : 'var(--bg-card)', color: conf?.action === action ? couleur : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem', textAlign: 'left' }}>
                  {libelle}
                  <span style={{ display: 'block', fontSize: '0.72rem', fontWeight: 400, opacity: 0.75, marginTop: 1 }}>{aide}</span>
                </button>
              );
              return (
                <div key={d.idx} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem', marginBottom: '0.75rem', background: 'var(--bg-elevated)' }}>
                  <p style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.5rem', fontSize: '0.88rem' }}>
                    Stagiaire {d.idx + 1} : {d.saisi?.prenom} {d.saisi?.nom_naissance || d.saisi?.nom_usage}
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem', fontSize: '0.8rem' }}>
                    <div style={{ background: 'rgba(245,158,11,0.08)', borderRadius: 6, padding: '0.5rem' }}>
                      <strong>Saisi dans la fiche :</strong><br />
                      {d.saisi?.prenom} {d.saisi?.nom_naissance || d.saisi?.nom_usage}<br />
                      {d.saisi?.date_naissance ? new Date(d.saisi.date_naissance).toLocaleDateString('fr-FR') : '—'}
                    </div>
                    <div style={{ background: 'rgba(59,130,246,0.08)', borderRadius: 6, padding: '0.5rem' }}>
                      <strong>Existant ({d.match.stagiaire_id}) :</strong><br />
                      {d.match.prenoms} {d.match.nom}<br />
                      {d.match.date_naissance ? new Date(d.match.date_naissance).toLocaleDateString('fr-FR') : '—'}
                    </div>
                  </div>
                  {d.proximite === 'debut' && (
                    <p style={{ fontSize: '0.78rem', color: '#b45309', background: 'rgba(245,158,11,0.12)', borderRadius: 6, padding: '0.4rem 0.6rem', margin: '0 0 0.6rem' }}>
                      ⚠ Prénoms proches mais pas identiques : vérifiez qu'il s'agit bien de la même personne avant de choisir « Mettre à jour l'existant » (le prénom enregistré sera remplacé par celui de la fiche).
                    </p>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {choix('remplacer', "✅ Mettre à jour l'existant", 'Écraser les données avec celles de la fiche', '#10b981', d.match.stagiaire_id)}
                    {choix('lier', '🔗 Lier sans modifier', "Associer à l'existant, conserver ses données actuelles", '#a78bfa', d.match.stagiaire_id)}
                    {choix('nouveau', '➕ Créer un nouveau stagiaire', 'Enregistrer comme un nouveau profil distinct', '#3b82f6', null)}
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => { setDoublons(null); setConfirmations([]); setValidatingId(null); }} className="btn-secondary" style={{ fontSize: '0.85rem' }}>Annuler</button>
              <button onClick={() => valider(doublons[0]?.fiche_id, confirmations)}
                disabled={confirmations.length < doublons.length}
                className="btn-primary" style={{ fontSize: '0.85rem', opacity: confirmations.length < doublons.length ? 0.5 : 1 }}>
                ✅ Confirmer et valider
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Accès formateur : statut de l'envoi du code PIN par SMS (partagé : une seule formulation partout) ─────────
// Le PIN n'est jamais dans l'e-mail (mesure de sécurité : lien et code sur deux canaux) ; quand le SMS n'est pas parti,
// on dit pourquoi et on rappelle qu'il faut transmettre le code autrement (il reste affiché dans l'application).
export function StatutSmsPin({ envoye, envoyeLe, diagnostic }) {
  if (envoye) {
    return <>✅ Code PIN envoyé par SMS{envoyeLe ? ` le ${new Date(envoyeLe).toLocaleDateString('fr-FR')}` : ''}</>;
  }
  return (
    <>
      ⚠ SMS non envoyé — {diagnostic || "cliquez sur « Renvoyer » pour réessayer : la cause exacte s'affichera"}
      <br />
      <span>Le code PIN n'est pas inclus dans l'e-mail : transmettez-le au formateur par un autre moyen.</span>
    </>
  );
}

// ── Carte « Convention » d'un groupe (session × client) : création, prix, envoi, régénération ─────────────────
// Commune à la session (un groupe = une convention). La remise fixe en € n'est jamais affichée ni modifiable.
const UNITES_CONVENTION = ['Stagiaire', 'Groupe', 'Heure', 'Forfait', 'Jour'];

export function CarteConvention({ sessionId, groupe, convention = null, anciennes = [], devisId = null, nbInscrits = 0, onChange }) {
  const { token } = useAuth();
  const API = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
  const APP = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [prixForm, setPrixForm] = useState(null);   // { unite, qte, pu_ht, taux_remise_pct } quand la fenêtre de prix est ouverte
  const [envoi, setEnvoi] = useState(null);         // { lien, email, corps, convocation } quand la fenêtre d'envoi est ouverte
  const [lienCopie, setLienCopie] = useState(false);

  async function appel(url, options = {}) {
    const r = await fetch(`${API}${url}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) },
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || 'Erreur');
    return d;
  }
  async function agir(fn, ok) {
    setBusy(true); setMsg('');
    try {
      const res = await fn();
      if (ok) setMsg('✓ ' + (typeof ok === 'function' ? ok(res) : ok));
      if (onChange) await onChange();
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setBusy(false); }
  }

  const c = convention;
  const signee = c?.statut === 'Validée';
  const statutLib = !c ? null : signee ? '✓ Signée' : c.statut === 'Envoyée' ? '📨 Envoyée' : '🔖 Brouillon';
  const statutCouleur = !c ? 'var(--text-tertiary)' : signee ? 'var(--accent-emerald)' : c.statut === 'Envoyée' ? '#f59e0b' : 'var(--text-secondary)';
  const px = c?.prix_calcule || null;
  const fmt = v => (v === null || v === undefined || v === '') ? '—' : parseFloat(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const lienClient = c?.lien_token ? `${APP}/convention-client/${c.lien_token}` : '';
  const nb = c ? (c.nb_inscrits ?? nbInscrits) : nbInscrits;

  function creer() {
    agir(() => appel('/conventions', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, client_id: groupe.client_id, ...(devisId ? { devis_id: devisId } : {}) }),
    }), devisId ? 'Convention créée à partir du devis' : 'Convention créée (prix catalogue + remise du client)');
  }
  function regenerer() {
    const message = c.devis_id
      ? 'Régénérer la convention : elle repart du devis (prix, remise, client) et repasse en brouillon. Le lien déjà envoyé sera invalidé. Continuer ?'
      : 'Régénérer la convention : elle repasse en brouillon. Le lien déjà envoyé sera invalidé. Continuer ?';
    if (!window.confirm(message)) return;
    agir(() => appel(`/conventions/${c.convention_id}/regenerer`, { method: 'POST' }),
      d => `Convention régénérée${d.lien_invalide ? ' (ancien lien invalidé)' : ''}`);
  }
  function nouvelle() {
    if (!window.confirm('Archiver la convention signée et créer une nouvelle convention (brouillon) calculée sur les inscrits actuels ?')) return;
    agir(() => appel(`/conventions/${c.convention_id}/nouvelle`, { method: 'POST' }), 'Convention signée archivée — nouvelle convention (brouillon) créée');
  }
  function ouvrirPrix() {
    const p = px || {};
    setPrixForm({
      unite: c.unite || p.unite || 'Stagiaire',
      qte: String(c.qte ?? p.qte ?? ''),
      pu_ht: String(c.pu_ht ?? p.pu_ht ?? ''),
      taux_remise_pct: String(c.taux_remise_pct ?? p.taux_remise_pct ?? 0),
    });
  }
  function enregistrerPrix() {
    const f = prixForm;
    if (!(parseFloat(f.pu_ht) >= 0)) { setMsg('❌ Prix unitaire invalide'); return; }
    agir(async () => {
      await appel(`/conventions/${c.convention_id}`, {
        method: 'PUT',
        body: JSON.stringify({ unite: f.unite, qte: f.qte, pu_ht: f.pu_ht, taux_remise_pct: f.taux_remise_pct === '' ? 0 : f.taux_remise_pct }),
      });
      setPrixForm(null);
    }, 'Prix de la convention modifié');
  }
  async function preparerEnvoi() {
    setBusy(true); setMsg('');
    try {
      const d = await appel(`/conventions/${c.convention_id}/generer-lien`, { method: 'POST', body: JSON.stringify({ email_client: c.email_client || undefined }) });
      setEnvoi({
        lien: d.lien,
        email: d.email_suggere || c.email_client || '', // règle « Convention » de Paramètres (principal / secondaire), sinon adresse déjà connue
        corps: `Bonjour,\n\nVeuillez trouver ci-dessous le lien pour consulter et signer votre convention de formation :\n\n${c.intitule_formation || ''}`,
        convocation: false,
      });
      if (onChange) await onChange();
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setBusy(false); }
  }
  function envoyerEmail() {
    if (!envoi.email) return;
    const cleaned = envoi.corps.replace(/\n\n📎[^\n]*/g, '').trimEnd();
    const corps = cleaned + (envoi.convocation
      ? '\n\n📎 Documents joints : programme de formation, convocation de formation + livret stagiaire.'
      : '\n\n📎 Le programme de formation est joint à cet email.');
    agir(async () => {
      const d = await appel(`/conventions/${c.convention_id}/envoyer-email`, {
        method: 'POST',
        body: JSON.stringify({ email: envoi.email, lien: envoi.lien, message_personnalise: corps, inclure_convocation: envoi.convocation }),
      });
      setEnvoi(null);
      return d;
    }, d => 'Convention envoyée' + (d.convocation_jointe ? ' + convocation jointe' : d.programme_joint ? ' + programme joint' : ''));
  }
  function copierLien() {
    try { navigator.clipboard.writeText(lienClient); setLienCopie(true); setTimeout(() => setLienCopie(false), 2000); } catch (e) {}
  }

  const btn = { padding: '0.25rem 0.7rem', fontSize: '0.8rem' };
  const champ = { width: '100%', padding: '0.45rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box' };
  const etiquette = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 3 };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '0.7rem 1rem', marginBottom: '0.8rem', background: 'var(--bg-card)' }}>
      {/* En-tête sur 2 lignes : à gauche (1) groupe + référence, (2) total ; à droite (1) état, (2) actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.4rem 1.2rem', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.3rem 0.6rem', flexWrap: 'wrap' }}>
            <span><strong>Groupe {groupe.groupe || ''}</strong> — {groupe.nom_client || 'Sans client'}</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{nb} stagiaire{nb > 1 ? 's' : ''}</span>
            {c && (
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{c.reference || c.convention_id}</strong>
                {c.nb_versions > 1 && <span style={{ marginLeft: 6, fontSize: '0.75rem' }}>v{c.version}/{c.nb_versions}</span>}
                <span style={{ marginLeft: 6, fontSize: '0.78rem' }}>{c.devis_id ? `(devis ${c.devis_id})` : '(sans devis)'}</span>
              </span>
            )}
          </div>
          {c && px && (
            <div style={{ fontSize: '0.88rem', marginTop: 3 }}>
              Total HT : <strong>{fmt(px.total_ht)} €</strong>
              <span style={{ marginLeft: 8, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                {parseFloat(px.qte)} {String(px.unite || '').toLowerCase()}{parseFloat(px.qte) > 1 ? 's' : ''} × {fmt(px.pu_ht)} €{parseFloat(px.taux_remise_pct) > 0 ? ` — remise ${parseFloat(px.taux_remise_pct)} %` : ''}
              </span>
            </div>
          )}
        </div>

        {c ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: statutCouleur }}>{statutLib}</span>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" style={btn} onClick={() => ouvrirApercuConvention(c.convention_id, `Convention ${c.reference || c.convention_id}`)}>👁 Aperçu</button>
              {!signee && <button className="btn-secondary" style={btn} disabled={busy} onClick={ouvrirPrix}>💶 Modifier le prix</button>}
              {!signee && <button className="btn-primary" style={btn} disabled={busy} onClick={preparerEnvoi}>📨 Envoyer par e-mail</button>}
              {!signee && lienClient && <button className="btn-secondary" style={btn} onClick={copierLien}>{lienCopie ? '✓ Copié' : '🔗 Copier le lien'}</button>}
              {!signee && <button className="btn-secondary" style={btn} disabled={busy} onClick={regenerer}>🔄 Régénérer</button>}
              {signee && <button className="btn-secondary" style={btn} disabled={busy} onClick={nouvelle}>🔁 Nouvelle convention</button>}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Aucune convention pour ce groupe.</span>
            {groupe.client_id ? (
              <>
                <button className="btn-primary" style={btn} disabled={busy} onClick={creer}>{busy ? '⏳...' : '📋 Créer la convention'}</button>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  {devisId ? `À partir du devis ${devisId}` : 'Sans devis : prix catalogue de la formation et remise habituelle du client'}
                </span>
              </>
            ) : (
              <span style={{ fontSize: '0.8rem', color: COULEUR_ATTENTION }}>⚠ Ce groupe n'est rattaché à aucun client : rattachez-le d'abord.</span>
            )}
          </div>
        )}
      </div>

      {c && signee && c.nb_stagiaires && nb !== c.nb_stagiaires && (
        <p style={{ fontSize: '0.8rem', color: COULEUR_ATTENTION, margin: '0.4rem 0 0' }}>
          ⚠ Convention signée sur {c.nb_stagiaires} stagiaire(s) — {nb} inscrit(s) aujourd'hui : générer une nouvelle convention.
        </p>
      )}
      {c && !signee && c.lien_token && (
        <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0.35rem 0 0' }}>
          Lien valable jusqu'au {formatDate(c.token_expire_at)}
          {c.dernier_envoi_date
            ? ` — envoyé à ${c.email_client || '?'} le ${formatDate(c.dernier_envoi_date)}`
            : (c.email_client ? ` — pas encore envoyé (adresse : ${c.email_client})` : ' — pas encore envoyé')}
        </p>
      )}

      {anciennes.length > 0 && (
        <div style={{ marginTop: '0.6rem', fontSize: '0.78rem' }}>
          <div style={{ fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 3 }}>Versions précédentes (PDF archivé)</div>
          {anciennes.map(a => (
            <div key={a.convention_id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: 2 }}>
              <span>v{a.version}{a.statut === 'Validée' ? ` — signée le ${formatDate(a.date_signature)}` : ''}{a.nb_stagiaires ? ` — ${a.nb_stagiaires} stagiaire(s)` : ''}</span>
              {a.statut === 'Validée' && (
                <button className="btn-secondary" style={{ padding: '0.1rem 0.5rem', fontSize: '0.75rem' }}
                  onClick={() => ouvrirDocument({ titre: `Convention ${a.reference || a.convention_id} v${a.version} (version archivée)`, url: `/conventions/${a.convention_id}/archive`, nomFichier: `CONVENTION_${a.reference || a.convention_id}_v${a.version}.pdf` })}>
                  👁 Aperçu
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {msg && (
        <div style={{ marginTop: '0.55rem', padding: '0.4rem 0.7rem', borderRadius: 6, fontSize: '0.82rem',
          ...styleMessage(niveauMessage(msg)) }}>{msg}</div>
      )}

      {/* ── Fenêtre : modifier le prix ── */}
      {prixForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.5rem', width: 440, maxWidth: '95vw' }}>
            <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>💶 Prix de la convention {c.reference || ''}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7rem' }}>
              <div>
                <label style={etiquette}>Unité</label>
                <select value={prixForm.unite} onChange={e => setPrixForm(f => ({ ...f, unite: e.target.value }))} style={champ}>
                  {[...new Set([prixForm.unite, ...UNITES_CONVENTION])].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label style={etiquette}>Quantité</label>
                <input type="number" step="0.5" min="0" value={prixForm.qte} disabled={prixForm.unite === 'Stagiaire'}
                  onChange={e => setPrixForm(f => ({ ...f, qte: e.target.value }))} style={{ ...champ, opacity: prixForm.unite === 'Stagiaire' ? 0.55 : 1 }} />
              </div>
              <div>
                <label style={etiquette}>Prix unitaire HT (€)</label>
                <input type="number" step="0.01" min="0" value={prixForm.pu_ht} onChange={e => setPrixForm(f => ({ ...f, pu_ht: e.target.value }))} style={champ} />
              </div>
              <div>
                <label style={etiquette}>Remise (%)</label>
                <input type="number" step="0.5" min="0" max="100" value={prixForm.taux_remise_pct} onChange={e => setPrixForm(f => ({ ...f, taux_remise_pct: e.target.value }))} style={champ} />
              </div>
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', margin: '0.7rem 0 0' }}>
              Pour l'unité « Stagiaire », la quantité suit le nombre de stagiaires inscrits du groupe. Le total est recalculé à l'enregistrement
              et la convention montre un prix unitaire net, avec la mention de la remise.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn-secondary" onClick={() => setPrixForm(null)}>Annuler</button>
              <button className="btn-primary" disabled={busy} onClick={enregistrerPrix}>{busy ? '⏳...' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Fenêtre : envoyer par e-mail ── */}
      {envoi && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.5rem', width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>📨 Envoyer la convention {c.reference || ''}</h3>
            <label style={etiquette}>Adresse(s) e-mail du client <span style={{ fontWeight: 400 }}>(plusieurs : séparées par une virgule — règle de Paramètres &gt; Envoi de documents)</span></label>
            <input type="email" multiple value={envoi.email} onChange={e => setEnvoi(v => ({ ...v, email: e.target.value }))} style={champ} placeholder="client@exemple.fr" />
            {!envoi.email && (
              <p style={{ fontSize: '0.75rem', color: COULEUR_ATTENTION, margin: '0.3rem 0 0' }}>
                Aucune adresse connue pour ce client : saisissez-la pour activer le bouton « Envoyer ».
              </p>
            )}
            <label style={{ ...etiquette, marginTop: '0.7rem' }}>Message</label>
            <textarea rows={6} value={envoi.corps} onChange={e => setEnvoi(v => ({ ...v, corps: e.target.value }))} style={{ ...champ, resize: 'vertical' }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '0.7rem', fontSize: '0.85rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={envoi.convocation} onChange={e => setEnvoi(v => ({ ...v, convocation: e.target.checked }))} />
              Joindre aussi la convocation et le livret stagiaire (le programme de formation est toujours joint)
            </label>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', margin: '0.6rem 0 0', wordBreak: 'break-all' }}>Lien : {envoi.lien}</p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn-secondary" onClick={() => setEnvoi(null)}>Annuler</button>
              <button className="btn-primary" disabled={busy || !envoi.email} onClick={envoyerEmail}>{busy ? '⏳ Envoi...' : '📨 Envoyer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Section repliable (accordéon). variante « carte » : cadre avec en-tête (pages de détail, liseré optionnel borderColor) ;
// variante « titre » : simple titre en majuscules avec flèche (onglets de Paramètres)
export function SectionRepliable({ title, defaultOpen = false, variante = 'carte', borderColor, actions, children }) {
  const [open, setOpen] = useState(defaultOpen);
  if (variante === 'titre') {
    return (
      <div style={{ margin: '1.2rem 0 0.4rem' }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.45rem',
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '0.25rem 0', width: '100%', textAlign: 'left',
            color: 'var(--text-secondary)',
            fontSize: '0.75rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em',
          }}>
          <span style={{
            display: 'inline-block',
            transition: 'transform 0.18s ease',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            fontSize: '0.65rem', lineHeight: 1, color: 'var(--text-tertiary)',
          }}>▶</span>
          {title}
        </button>
        {open && <div style={{ marginTop: '0.6rem' }}>{children}</div>}
      </div>
    );
  }
  return (
    <div style={{ border: `1px solid ${borderColor || 'var(--border)'}`, borderRadius: '10px', marginBottom: '1rem', overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ padding: '0.65rem 1rem', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none', borderBottom: open ? '1px solid var(--border)' : 'none', borderLeft: borderColor ? `4px solid ${borderColor}` : undefined }}
      >
        <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)', flex: 1 }}>{title}</span>
        {/* actions : boutons de l'en-tête (ex. « + Nouveau devis »), sans déplier/replier la section au clic */}
        {actions && <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', gap: '0.4rem' }}>{actions}</span>}
        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', transition: 'transform 0.2s', display: 'inline-block', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▼</span>
      </div>
      {open && <div>{children}</div>}
    </div>
  );
}

// ── Jours non travaillés : jours fériés de France + fermetures CEJ (Paramètres → Règles) ────────────────────────────────────────
// Map 'YYYY-MM-DD' → { type: 'ferie' | 'fermeture', libelle } sur l'année précédente et les 3 suivantes (formulaires de session)
export function useJoursNonTravailles() {
  const [jours, setJours] = useState(() => new Map());
  useEffect(() => {
    let annule = false;
    const a = new Date().getFullYear();
    apiFetch(`/jours-non-travailles?du=${a - 1}-01-01&au=${a + 3}-12-31`)
      .then(r => { if (!annule) setJours(new Map((r.jours || []).map(j => [j.date, j]))); })
      .catch(() => { /* sans cette liste, aucune alerte : jamais bloquant */ });
    return () => { annule = true; };
  }, []);
  return jours;
}
// Texte d'alerte d'une date (ou null) : « Armistice 1918 (jour férié) » / « Pont de mai (fermeture CEJ) »
export function libelleJourNonTravaille(jours, iso) {
  const j = jours && jours.get(iso);
  return j ? `${j.libelle} (${j.type === 'ferie' ? 'jour férié' : 'fermeture CEJ'})` : null;
}

// ── Signature numérisée du formateur : pastille d'état + zone de tracé ──────────────────────────────────────────────
export const ETATS_SIGNATURE_FORMATEUR = {
  valide:       { icone: '✍', couleur: '#16a34a', libelle: 'Signature enregistrée' },
  a_renouveler: { icone: '⚠', couleur: '#d97706', libelle: 'Signature à renouveler' },
  en_attente:   { icone: '⏳', couleur: '#64748b', libelle: 'Demande envoyée — en attente de la signature du formateur' },
  aucune:       { icone: '—', couleur: 'var(--text-tertiary)', libelle: 'Aucune signature enregistrée (attestation à scanner)' },
};
// signature : { etat, echeance } renvoyé par l'API (liste et fiche formateur)
export function BadgeSignatureFormateur({ signature, avecLibelle = false }) {
  const e = ETATS_SIGNATURE_FORMATEUR[signature?.etat] || ETATS_SIGNATURE_FORMATEUR.aucune;
  const echeance = signature?.echeance ? ` — échéance le ${new Date(signature.echeance).toLocaleDateString('fr-FR')}` : '';
  return (
    <span title={`${e.libelle}${echeance}`} style={{ color: e.couleur, fontWeight: 700, fontSize: avecLibelle ? '0.85rem' : '1.05rem', whiteSpace: 'nowrap' }}>
      {e.icone}{avecLibelle ? ` ${e.libelle}` : ''}
    </span>
  );
}

// Tracé de signature lissé, partagé par toutes les zones de signature (doigt, stylet ou souris) : renvoie les gestionnaires d'événements à poser sur le <canvas>.
// Le navigateur ne signale qu'un point par image (≈ 60 par seconde) : relier ces points par des droites donne des arrondis en polygone.
// On récupère donc tous les points intermédiaires (getCoalescedEvents) et on trace des courbes passant par les milieux des segments.
export function useTraceSignature(canvasRef, { largeurTrait = 3, onDebut } = {}) {
  const etat = React.useRef({ actif: false, dernier: null, milieu: null });
  const pos = e => { const r = canvasRef.current.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const debut = e => {
    e.preventDefault();
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* non supporté */ }
    const p = pos(e); const ctx = canvasRef.current.getContext('2d');
    ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#1a3668'; ctx.fillStyle = '#1a3668'; ctx.lineWidth = largeurTrait;
    etat.current = { actif: true, dernier: p, milieu: p };
    ctx.beginPath(); ctx.arc(p.x, p.y, largeurTrait / 2, 0, Math.PI * 2); ctx.fill();
    if (onDebut) onDebut();
  };
  const ajouter = p => {
    const s = etat.current; const d = s.dernier;
    if (Math.hypot(p.x - d.x, p.y - d.y) < 1) return;
    const m = { x: (d.x + p.x) / 2, y: (d.y + p.y) / 2 };
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath(); ctx.moveTo(s.milieu.x, s.milieu.y); ctx.quadraticCurveTo(d.x, d.y, m.x, m.y); ctx.stroke();
    s.dernier = p; s.milieu = m;
  };
  const trace = e => {
    if (!etat.current.actif) return;
    e.preventDefault();
    const intermediaires = typeof e.nativeEvent.getCoalescedEvents === 'function' ? e.nativeEvent.getCoalescedEvents() : [];
    (intermediaires.length ? intermediaires : [e.nativeEvent]).forEach(ev => ajouter(pos(ev)));
  };
  const fin = () => {
    const s = etat.current;
    if (!s.actif) return;
    s.actif = false;
    // dernier tronçon : du dernier milieu jusqu'au dernier point
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath(); ctx.moveTo(s.milieu.x, s.milieu.y); ctx.lineTo(s.dernier.x, s.dernier.y); ctx.stroke();
  };
  return { onPointerDown: debut, onPointerMove: trace, onPointerUp: fin, onPointerCancel: fin, onPointerLeave: fin };
}

// Zone de signature au doigt (smartphone) ou à la souris. onSave(dataUrl PNG transparent, recadré sur le tracé).
// controleQualite : refuse un tracé qui n'occupe pas au moins la moitié de la largeur et 20 % de la hauteur du cadre.
export function SignatureCanvas({ onSave, onCancel, hauteur = 220, labelValider = 'Continuer', controleQualite = false }) {
  const canvasRef = React.useRef(null);
  const largeurCadre = React.useRef(0);
  const [erreur, setErreur] = useState('');
  const gestionnaires = useTraceSignature(canvasRef, { largeurTrait: 3, onDebut: () => setErreur('') });

  const initialiser = React.useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const r = c.getBoundingClientRect();
    c.width = Math.round(r.width * dpr);
    c.height = Math.round(r.height * dpr);
    c.getContext('2d').scale(dpr, dpr);
    largeurCadre.current = r.width;
  }, []);
  useEffect(() => {
    initialiser();
    // un changement d'orientation redimensionne le cadre : on repart d'une page blanche plutôt que de déformer le tracé
    const surResize = () => { const c = canvasRef.current; if (c && Math.abs(c.getBoundingClientRect().width - largeurCadre.current) > 2) { initialiser(); setErreur(''); } };
    window.addEventListener('resize', surResize);
    return () => window.removeEventListener('resize', surResize);
  }, [initialiser]);

  const effacer = () => { initialiser(); setErreur(''); };

  function valider() {
    const c = canvasRef.current; const W = c.width; const H = c.height;
    const px = c.getContext('2d').getImageData(0, 0, W, H).data;
    let x0 = W, y0 = H, x1 = -1, y1 = -1;
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      if (px[(y * W + x) * 4 + 3] > 40) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    }
    if (x1 < 0) { setErreur('Veuillez signer dans le cadre avant de continuer.'); return; }
    const w = x1 - x0 + 1, h = y1 - y0 + 1;
    if (controleQualite && (w < W * 0.5 || h < H * 0.2)) { setErreur('Signature trop petite : signez en grand, sur la majeure partie du cadre (téléphone en paysage).'); return; }
    const m = 8; const ox = Math.max(0, x0 - m); const oy = Math.max(0, y0 - m);
    const cw = Math.min(W, x1 + m + 1) - ox; const ch = Math.min(H, y1 + m + 1) - oy;
    const sortie = document.createElement('canvas'); sortie.width = cw; sortie.height = ch;
    sortie.getContext('2d').drawImage(c, ox, oy, cw, ch, 0, 0, cw, ch);
    onSave(sortie.toDataURL('image/png'));
  }

  const btn = (fond, couleur) => ({ flex: 1, padding: '0.7rem', borderRadius: 8, border: fond === 'transparent' ? '1px solid #cbd5e1' : 'none', background: fond, color: couleur, fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ position: 'relative' }}>
        <canvas ref={canvasRef}
          style={{ width: '100%', height: hauteur, border: '2px solid #1a3668', borderRadius: 10, background: '#fff', touchAction: 'none', cursor: 'crosshair', display: 'block' }}
          {...gestionnaires} />
        <div aria-hidden="true" style={{ position: 'absolute', left: 16, right: 16, bottom: Math.round(hauteur * 0.28), borderTop: '1px dashed #cbd5e1', pointerEvents: 'none' }} />
      </div>
      {erreur && <div role="alert" style={{ color: '#b91c1c', fontSize: '0.88rem', fontWeight: 600 }}>{erreur}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={effacer} style={btn('transparent', '#475569')}>Effacer</button>
        {onCancel && <button type="button" onClick={onCancel} style={btn('transparent', '#475569')}>Annuler</button>}
        <button type="button" onClick={valider} style={{ ...btn('#1a3668', '#fff'), flex: 2 }}>{labelValider}</button>
      </div>
    </div>
  );
}

// ── SMS de session : cartes d'action, badges, résultat, historique, sélecteur de stagiaires ───────────────────
// Types d'envoi (badge de l'historique) — un seul endroit pour les couleurs et libellés
export const TYPES_SMS = {
  confirmation:       { bg: 'rgba(16,185,129,0.12)',  color: 'var(--accent-emerald)', label: 'confirmation' },
  rappel:             { bg: 'rgba(251,146,60,0.14)',  color: '#fb923c',               label: 'rappel' },
  report:             { bg: 'rgba(167,139,250,0.14)', color: 'var(--accent-violet)',  label: 'report' },
  annulation:         { bg: 'rgba(248,113,113,0.14)', color: '#f87171',               label: 'annulation' },
  changement_session: { bg: 'rgba(34,211,238,0.14)',  color: 'var(--accent-cyan)',    label: 'changement' },
  libre:              { bg: 'rgba(74,158,255,0.14)',  color: 'var(--accent-blue)',    label: 'message libre' },
};
// Envois groupés (tous les inscrits avec numéro) : une ligne de configuration par carte
export const ACTIONS_SMS_GROUPE = [
  { type: 'confirmation', titre: "✅ Confirmation d'inscription", couleur: '#1a3668', libelleBouton: '📨 Envoyer la confirmation', libelleConfirmation: 'les SMS de confirmation',
    description: "Envoie un SMS de confirmation à tous les stagiaires inscrits disposant d'un numéro de téléphone." },
  { type: 'rappel', titre: '🔔 Rappel de formation', couleur: '#E87722', libelleBouton: '📣 Envoyer le rappel', libelleConfirmation: 'les rappels SMS',
    description: 'Envoie un SMS de rappel (J-7 ou J-1 selon votre choix) à tous les stagiaires avec numéro.' },
  { type: 'report', titre: '📅 Report de session', couleur: '#7c3aed', libelleBouton: '📅 Envoyer le report', libelleConfirmation: 'les SMS de report de session',
    description: "Envoie un SMS de report à tous les inscrits avec numéro (envoyé aussi automatiquement lors d'un changement de date)." },
];

// Longueur d'un SMS : 160 caractères par SMS (153 si plusieurs) ; un caractère hors alphabet GSM (ç minuscule, â, ê, œ, « », emoji…) passe le SMS en
// Unicode : 70 caractères par SMS (67 si plusieurs). → { caracteres, segments, special }
const GSM_BASE = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_ETENDU = '^{}\\[~]|€';
export function compterSms(texte) {
  const t = String(texte || '');
  const chars = Array.from(t);
  const special = chars.some(c => !GSM_BASE.includes(c) && !GSM_ETENDU.includes(c));
  if (special) return { caracteres: chars.length, segments: chars.length === 0 ? 0 : t.length <= 70 ? 1 : Math.ceil(t.length / 67), special: true };
  const unites = chars.reduce((n, c) => n + (GSM_ETENDU.includes(c) ? 2 : 1), 0);
  return { caracteres: chars.length, segments: unites === 0 ? 0 : unites <= 160 ? 1 : Math.ceil(unites / 153), special: false };
}

// Carte d'action SMS : titre, description, contenu (children) et bouton coloré en bas (les cartes d'une même rangée ont la même hauteur)
export function CarteSms({ titre, description, couleur, libelleBouton, onClick, desactive, enCours, bloque, largeur = 1, children }) {
  const inactif = desactive || bloque;
  return (
    <div style={{ flex: largeur, minWidth: '240px', display: 'flex', flexDirection: 'column', padding: '1rem', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)' }}>
      <div style={{ fontWeight: 700, marginBottom: '0.4rem' }}>{titre}</div>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0 0 0.6rem' }}>{description}</p>
      {children && <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>{children}</div>}
      <button onClick={onClick} disabled={inactif}
        style={{ marginTop: 'auto', alignSelf: 'flex-start', padding: '0.45rem 1rem', borderRadius: '8px', border: 'none', background: desactive ? 'var(--border)' : couleur, color: '#fff',
          fontWeight: 700, fontSize: '0.85rem', cursor: desactive ? 'not-allowed' : 'pointer', opacity: bloque && !desactive ? 0.7 : 1 }}>
        {enCours ? '⏳ Envoi…' : libelleBouton}
      </button>
    </div>
  );
}

// Menu déroulant de choix des stagiaires, avec cases à cocher : un, plusieurs ou tous
// stagiaires : [{ id, nom }] ; selection : null = TOUS, sinon tableau d'ids ; onChange(null | ids)
export function MenuStagiairesMulti({ stagiaires, selection, onChange, libelleTous = 'Tous les stagiaires', ouvertInitial = false }) {
  const [ouvert, setOuvert] = useState(ouvertInitial);
  const ref = React.useRef(null);
  useEffect(() => {
    if (!ouvert) return undefined;
    const fermer = e => { if (ref.current && !ref.current.contains(e.target)) setOuvert(false); };
    document.addEventListener('mousedown', fermer);
    return () => document.removeEventListener('mousedown', fermer);
  }, [ouvert]);
  const ids = stagiaires.map(s => String(s.id));
  const courant = selection === null ? ids : selection.filter(i => ids.includes(i));
  const tous = ids.length > 0 && courant.length === ids.length;
  const libelle = ids.length === 0 ? 'Aucun stagiaire avec numéro'
    : tous ? `${libelleTous} (${ids.length})`
    : courant.length === 0 ? '— Sélectionner des stagiaires —'
    : courant.length === 1 ? (stagiaires.find(s => String(s.id) === courant[0])?.nom || '1 stagiaire')
    : `${courant.length} stagiaires sélectionnés`;
  const basculer = id => {
    const suivant = courant.includes(id) ? courant.filter(x => x !== id) : [...courant, id];
    onChange(suivant.length === ids.length ? null : suivant);
  };
  const ligne = { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.7rem', cursor: 'pointer', fontSize: '0.84rem', color: 'var(--text-primary)' };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => ids.length && setOuvert(o => !o)}
        style={{ width: '100%', textAlign: 'left', padding: '0.4rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)',
          color: 'var(--text-primary)', fontSize: '0.84rem', cursor: ids.length ? 'pointer' : 'not-allowed', display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{libelle}</span><span aria-hidden="true">▾</span>
      </button>
      {ouvert && (
        <div style={{ position: 'absolute', zIndex: 50, left: 0, right: 0, marginTop: 2, maxHeight: 260, overflowY: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, boxShadow: 'var(--shadow)' }}>
          <label style={{ ...ligne, fontWeight: 700, borderBottom: '1px solid var(--border)' }}>
            <input type="checkbox" checked={tous} onChange={() => onChange(tous ? [] : null)} /> {libelleTous}
          </label>
          {stagiaires.map(s => (
            <label key={s.id} style={ligne}>
              <input type="checkbox" checked={courant.includes(String(s.id))} onChange={() => basculer(String(s.id))} /> {s.nom}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Résultat du dernier envoi (vert = tout est parti, ambre = envoi partiel, rouge = échec)
export function ResultatSms({ result }) {
  if (!result) return null;
  const niveau = !result.ok ? 'echec' : result.erreurs > 0 ? 'attention' : 'succes';
  return (
    <div style={{ padding: '0.8rem 1.1rem', borderRadius: '8px', fontSize: '0.88rem', ...styleMessage(niveau) }}>
      {result.ok ? (
        <>
          <div>{`${result.erreurs > 0 ? '⚠' : '✅'} ${result.envoyés} SMS envoyé(s)${result.erreurs > 0 ? `, ${result.erreurs} erreur(s)` : ''}.`}</div>
          {result.erreurDetails?.length > 0 && (
            <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem', fontSize: '0.82rem' }}>
              {result.erreurDetails.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          )}
        </>
      ) : `❌ Erreur : ${result.error}`}
    </div>
  );
}

// Tableau « Historique des envois » d'une session
export function HistoriqueSms({ lignes }) {
  const th = { padding: '0.5rem 0.8rem', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
  const td = { padding: '0.45rem 0.8rem' };
  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.6rem' }}>📋 Historique des envois</div>
      {lignes.length === 0 ? (
        <div style={{ padding: '1.2rem', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', borderRadius: '8px', fontSize: '0.85rem' }}>
          Aucun SMS envoyé pour cette session.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-elevated)', borderBottom: '2px solid var(--border)' }}>
                {['Date', 'Type', 'Stagiaire', 'Destinataire', 'Statut', 'Message'].map(h => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {lignes.map(s => {
                const b = TYPES_SMS[s.type] || { bg: 'var(--bg-elevated)', color: 'var(--text-secondary)', label: s.type };
                const ok = s.statut === 'envoyé';
                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{new Date(s.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={td}><span style={{ padding: '0.15rem 0.5rem', borderRadius: '12px', fontSize: '0.78rem', fontWeight: 600, background: b.bg, color: b.color }}>{b.label}</span></td>
                    <td style={td}>{s.nom_stagiaire || '—'}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{s.destinataire}</td>
                    <td style={td}>
                      <span style={{ color: couleurMessage(ok), fontWeight: 600 }}>{ok ? '✓' : '✗'} {s.statut}</span>
                      {s.erreur && <div style={{ color: COULEUR_ECHEC, fontSize: '0.78rem' }}>{s.erreur}</div>}
                    </td>
                    <td style={{ ...td, maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }} title={s.message}>{s.message}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function formatCurrency(val) {
  if (!val && val !== 0) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val);
}

export function formatDate(val) {
  if (!val) return '—';
  const d = new Date(val);
  return d.toLocaleDateString('fr-FR');
}
